import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import {
  fetchConfig,
  debounce,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #debouncedOnCartQuantityChange = debounce(this.#onCartQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#onCartQuantityClick);
    this.addEventListener('beforeinput', this.#onCartQuantityBeforeInput);
    this.addEventListener('input', this.#onCartQuantityInput);
    this.addEventListener('change', this.#debouncedOnCartQuantityChange);
    this.addEventListener('keydown', this.#onCartQuantityKeyDown);
    this.addEventListener('submit', this.#onCartFormSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#onCartQuantityClick);
    this.removeEventListener('beforeinput', this.#onCartQuantityBeforeInput);
    this.removeEventListener('input', this.#onCartQuantityInput);
    this.removeEventListener('change', this.#debouncedOnCartQuantityChange);
    this.removeEventListener('keydown', this.#onCartQuantityKeyDown);
    this.removeEventListener('submit', this.#onCartFormSubmit);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles cart-quantity button clicks.
   * @param {Event} event - The event.
   */
  #onCartQuantityClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('cart-quantity .quantity__button');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();

    const quantityRoot = button.closest('cart-quantity');
    const input = quantityRoot?.querySelector('input.quantity__input[name="updates[]"]');
    if (!(input instanceof HTMLInputElement)) return;

    const currentQuantity = Number(input.value) || 0;
    const direction = button.getAttribute('name');
    const nextQuantity = direction === 'plus' ? currentQuantity + 1 : Math.max(currentQuantity - 1, 0);

    input.value = String(nextQuantity);
    this.#updateCartQuantityInput(input);
  };

  /**
   * Handles direct cart-quantity input changes.
   * @param {Event} event - The event.
   */
  #onCartQuantityChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.closest('cart-quantity')) return;

    this.#updateCartQuantityInput(event.target);
  }

  /**
   * Prevents non-digit text from being entered into cart quantity inputs.
   * @param {InputEvent} event
   */
  #onCartQuantityBeforeInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.closest('cart-quantity')) return;
    if (event.inputType.startsWith('delete')) return;
    if (event.data === null) return;

    if (/^\d+$/.test(event.data) === false) {
      event.preventDefault();
    }
  };

  /**
   * Sanitizes pasted or autofilled cart quantity values down to digits only.
   * @param {Event} event
   */
  #onCartQuantityInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.closest('cart-quantity')) return;

    const sanitizedValue = event.target.value.replace(/\D+/g, '');
    if (sanitizedValue === event.target.value) return;

    event.target.value = sanitizedValue;
  };

  /**
   * Prevents Enter inside cart quantity inputs from submitting the cart form.
   * Blurring the input preserves the existing change-driven Ajax update flow.
   * @param {KeyboardEvent} event
   */
  #onCartQuantityKeyDown = (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.closest('cart-quantity')) return;

    event.preventDefault();
    event.target.blur();
  };

  /**
   * Prevents the cart quantity form from falling back to a full page submit.
   * @param {SubmitEvent} event
   */
  #onCartFormSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!event.target.matches('form.cart-form')) return;

    event.preventDefault();
  };

  /**
   * Updates the cart from a cart-quantity input.
   * @param {HTMLInputElement} input - The quantity input.
   */
  #updateCartQuantityInput(input) {
    const line = Number(input.dataset.index || 0);
    if (!line) return;

    const quantity = Math.max(Math.round(Number(input.value) || 0), 0);
    input.value = String(quantity);

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The triggering event.
   */
  onLineItemRemove(line, event) {
    event?.preventDefault();

    const lineNumber = Number(line);
    if (!lineNumber) return;

    this.updateQuantity({
      line: lineNumber,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const removeButton = this.#getLineItemRemoveButton(config.line);
    this.#toggleRemoveButtonLoading(removeButton, true);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const currentSectionHtml = parsedResponseText.sections[this.sectionId];
        const didReplaceDrawerContent =
          this.isDrawer && (await this.#replaceDrawerContentFromSectionHtml(currentSectionHtml));

        if (!didReplaceDrawerContent) {
          morphSection(this.sectionId, currentSectionHtml, this.isDrawer ? 'hydration' : 'full', {
            injectStylesheet: true,
          });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#toggleRemoveButtonLoading(removeButton, false);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {Event} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.add('is-visible');
  };

  /**
   * Handles the cart update.
   *
   * @param {Event | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event.target === this) return;

    const detail = 'detail' in event ? event.detail : null;
    const sectionMap = detail?.data?.sections || detail?.resource?.sections;
    const cartItemsHtml = sectionMap?.[this.sectionId];

    if (cartItemsHtml) {
      const didReplaceDrawerContent =
        this.isDrawer && (await this.#replaceDrawerContentFromSectionHtml(cartItemsHtml));

      if (!didReplaceDrawerContent) {
        morphSection(this.sectionId, cartItemsHtml, this.isDrawer ? 'hydration' : 'full', {
          injectStylesheet: true,
        });
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * @param {number} line
   * @returns {HTMLElement | null}
   */
  #getLineItemRemoveButton(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!(lineItemRow instanceof HTMLElement)) return null;

    const removeButton = lineItemRow.querySelector('[data-cart-remove-button]');
    return removeButton instanceof HTMLElement ? removeButton : null;
  }

  /**
   * @param {HTMLElement | null} removeButton
   * @param {boolean} isLoading
   */
  #toggleRemoveButtonLoading(removeButton, isLoading) {
    if (!(removeButton instanceof HTMLElement)) return;

    removeButton.classList.toggle('loading', isLoading);

    if (isLoading) {
      removeButton.setAttribute('aria-busy', 'true');
      removeButton.setAttribute('aria-disabled', 'true');
      removeButton.dataset.previousTabindex = removeButton.getAttribute('tabindex') || '';
      removeButton.setAttribute('tabindex', '-1');
      return;
    }

    removeButton.removeAttribute('aria-busy');
    removeButton.removeAttribute('aria-disabled');

    if (removeButton.dataset.previousTabindex === '') {
      removeButton.removeAttribute('tabindex');
    } else {
      removeButton.setAttribute('tabindex', removeButton.dataset.previousTabindex);
    }

    delete removeButton.dataset.previousTabindex;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Replaces live drawer content with the rendered content stored inside the drawer template.
   * Section Rendering responses for the header keep cart drawer content inside a template,
   * so hydration mode alone cannot reach the updated price and subtotal nodes.
   * @param {string | undefined} sectionHtml
   * @returns {Promise<boolean>}
   */
  async #replaceDrawerContentFromSectionHtml(sectionHtml) {
    if (!this.isDrawer || typeof sectionHtml !== 'string' || sectionHtml === '') return false;

    const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const currentSection = document.getElementById(`shopify-section-${this.sectionId}`);
    const nextSection = parsedDocument.getElementById(`shopify-section-${this.sectionId}`);

    if (!(currentSection instanceof HTMLElement) || !(nextSection instanceof HTMLElement)) return false;

    const currentComponent = currentSection.querySelector('cart-items-component');
    const nextComponent = nextSection.querySelector('cart-items-component');
    const nextTemplate = nextSection.querySelector('template[data-cart-drawer-content-template]');
    if (!(nextTemplate instanceof HTMLTemplateElement)) return false;

    const nextContentRoot = document.createElement('div');
    nextContentRoot.append(nextTemplate.content.cloneNode(true));

    const currentBody = currentSection.querySelector('[data-hydration-key="cart-drawer-body"]');
    const nextBody = nextContentRoot.querySelector('[data-hydration-key="cart-drawer-body"]');
    const currentFooter = currentSection.querySelector('[data-hydration-key="cart-drawer-footer"]');
    const nextFooter = nextContentRoot.querySelector('[data-hydration-key="cart-drawer-footer"]');

    if (!(nextBody instanceof HTMLElement)) return false;

    if (!(currentBody instanceof HTMLElement)) {
      if (!(currentComponent instanceof HTMLElement) || !(nextComponent instanceof HTMLElement)) return false;

      const componentChildren = document.createDocumentFragment();
      Array.from(nextComponent.childNodes).forEach((node) => {
        componentChildren.append(node.cloneNode(true));
      });

      currentComponent.replaceChildren(componentChildren);
      currentComponent.updatedCallback?.();

      return true;
    }

    const panelState = this.#captureDrawerPanelState(currentFooter);
    const shouldAnimateEmptyStateChange = this.#shouldAnimateDrawerEmptyStateChange(currentBody, nextBody);

    const replaceContent = () => {
      const bodyReplacement = /** @type {HTMLElement} */ (nextBody.cloneNode(true));
      morph(currentBody, bodyReplacement, {
        childrenOnly: false,
      });

      if (currentFooter instanceof HTMLElement) {
        if (nextFooter instanceof HTMLElement) {
          const footerReplacement = /** @type {HTMLElement} */ (nextFooter.cloneNode(true));
          this.#restoreDrawerPanelState(footerReplacement, panelState);
          morph(currentFooter, footerReplacement, {
            childrenOnly: false,
          });
        } else {
          currentFooter.remove();
        }
      } else if (nextFooter instanceof HTMLElement) {
        const footerInsertion = nextFooter.cloneNode(true);
        this.#restoreDrawerPanelState(footerInsertion, panelState);
        currentBody.after(footerInsertion);
      }
    };

    if (shouldAnimateEmptyStateChange) {
      await this.#animateDrawerEmptyStateChange(currentSection, currentBody, currentFooter, replaceContent);
    } else {
      replaceContent();
    }

    return true;
  }

  /**
   * @param {HTMLElement} currentSection
   * @param {HTMLElement} currentBody
   * @param {HTMLElement | null} currentFooter
   * @param {() => void} replaceContent
   */
  async #animateDrawerEmptyStateChange(currentSection, currentBody, currentFooter, replaceContent) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      replaceContent();
      return;
    }

    await this.#animateDrawerElements(
      [currentBody, currentFooter],
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(8px)' },
      ],
      { duration: 140, easing: 'ease-out', fill: 'forwards' }
    );

    replaceContent();

    const nextBody = currentSection.querySelector('[data-hydration-key="cart-drawer-body"]');
    const nextFooter = currentSection.querySelector('[data-hydration-key="cart-drawer-footer"]');

    await this.#animateDrawerElements(
      [nextBody, nextFooter],
      [
        { opacity: 0, transform: 'translateY(-8px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 180, easing: 'ease-out', fill: 'both' }
    );
  }

  /**
   * @param {(HTMLElement | null | Element)[]} elements
   * @param {Keyframe[]} keyframes
   * @param {KeyframeAnimationOptions} options
   */
  async #animateDrawerElements(elements, keyframes, options) {
    const animations = elements
      .filter((element) => element instanceof HTMLElement && element.isConnected)
      .map((element) => {
        return element.animate(keyframes, options).finished.catch(() => undefined);
      });

    if (animations.length === 0) return;

    await Promise.all(animations);
  }

  /**
   * @param {HTMLElement} currentBody
   * @param {HTMLElement} nextBody
   * @returns {boolean}
   */
  #shouldAnimateDrawerEmptyStateChange(currentBody, nextBody) {
    return this.#isDrawerEmptyState(currentBody) !== this.#isDrawerEmptyState(nextBody);
  }

  /**
   * @param {ParentNode} root
   * @returns {boolean}
   */
  #isDrawerEmptyState(root) {
    return root.querySelector('.drawer__panel--cart-empty') instanceof HTMLElement;
  }

  /**
   * @param {HTMLElement | null} footer
   * @returns {{ isOpen: boolean, activePanel: string, noteValue: string }}
   */
  #captureDrawerPanelState(footer) {
    if (!(footer instanceof HTMLElement)) {
      return {
        isOpen: false,
        activePanel: '',
        noteValue: '',
      };
    }

    const panels = footer.querySelector('cart-function-panels');
    const noteField = footer.querySelector('textarea[name="note"]');

    return {
      isOpen: panels instanceof HTMLElement ? panels.hasAttribute('data-open') : false,
      activePanel:
        panels instanceof HTMLElement && panels.hasAttribute('data-open') ? panels.dataset.activePanel || '' : '',
      noteValue: noteField instanceof HTMLTextAreaElement ? noteField.value : '',
    };
  }

  /**
   * @param {HTMLElement | null} footer
   * @param {{ isOpen: boolean, activePanel: string, noteValue: string }} state
   */
  #restoreDrawerPanelState(footer, state) {
    if (!(footer instanceof HTMLElement)) return;

    const panels = footer.querySelector('cart-function-panels');
    const wrapper = footer.querySelector('[data-cart-function-panel-wrapper]');
    const panelsAreOpen = state.isOpen && state.activePanel !== '';

    if (panels instanceof HTMLElement) {
      if (panelsAreOpen) {
        panels.setAttribute('data-open', 'true');
        panels.dataset.activePanel = state.activePanel;
      } else {
        panels.removeAttribute('data-open');
        delete panels.dataset.activePanel;
      }
    }

    if (wrapper instanceof HTMLElement) {
      if (panelsAreOpen) {
        wrapper.setAttribute('data-open', 'true');
      } else {
        wrapper.removeAttribute('data-open');
      }

      wrapper.style.height = panelsAreOpen ? '' : '0px';
    }

    footer.querySelectorAll('[data-cart-function-content]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;

      if (panelsAreOpen && panel.dataset.cartFunctionContent === state.activePanel) {
        panel.setAttribute('data-active', 'true');
        return;
      }

      panel.removeAttribute('data-active');
    });

    footer.querySelectorAll('[data-cart-function-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      const isActive = panelsAreOpen && trigger.dataset.cartFunctionPanel === state.activePanel;
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    const noteField = footer.querySelector('textarea[name="note"]');
    if (noteField instanceof HTMLTextAreaElement) {
      noteField.value = state.noteValue;
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
